<template>
  <div class="guardrail-card" :class="`guardrail-card--${categoryClass}`">
    <div class="guardrail-card-header">
      <i :class="icon" class="guardrail-card-icon"></i>
      <span class="guardrail-card-title">{{ title }}</span>
    </div>

    <!-- H1: crisis protocol route -->
    <div
      v-if="route && route.type === 'crisis_protocol'"
      class="guardrail-card-body"
    >
      <span class="guardrail-card-text">
        Follow your agency's crisis protocol and notify your supervisor now.
      </span>
      <div class="guardrail-card-actions">
        <a
          v-if="route.link && route.configured"
          :href="route.link"
          target="_blank"
          rel="noopener noreferrer"
          class="guardrail-card-btn guardrail-card-btn--primary"
        >
          <i class="fas fa-external-link-alt"></i> Crisis protocol
        </a>
        <span
          v-else-if="route.link"
          class="guardrail-card-btn guardrail-card-btn--placeholder"
          title="Placeholder — not yet configured by your agency"
        >
          <i class="fas fa-external-link-alt"></i> Crisis protocol
        </span>

        <a
          v-if="route.phone && route.configured"
          :href="`tel:${route.phone}`"
          class="guardrail-card-btn guardrail-card-btn--primary"
        >
          <i class="fas fa-phone"></i> {{ route.phone }}
        </a>
        <span
          v-else-if="route.phone"
          class="guardrail-card-btn guardrail-card-btn--placeholder"
          title="Placeholder — not yet configured by your agency"
        >
          <i class="fas fa-phone"></i> {{ route.phone }}
        </span>
      </div>
    </div>

    <!-- H2: prescriber route -->
    <div
      v-else-if="route && route.type === 'prescriber'"
      class="guardrail-card-body"
    >
      <span class="guardrail-card-text">
        Direct medication questions to the prescribing clinician or pharmacist.
      </span>
    </div>

    <!-- H3: policy + supervisor route -->
    <div
      v-else-if="route && route.type === 'policy_supervisor'"
      class="guardrail-card-body"
    >
      <span class="guardrail-card-text">
        Review the reporting criteria and raise it with your supervisor.
      </span>
      <div class="guardrail-card-actions">
        <a
          v-if="route.link && route.configured"
          :href="route.link"
          target="_blank"
          rel="noopener noreferrer"
          class="guardrail-card-btn"
        >
          <i class="fas fa-file-alt"></i>
          {{ route.label || "Agency reporting policy" }}
        </a>
        <span
          v-else-if="route.link"
          class="guardrail-card-btn guardrail-card-btn--placeholder"
          title="Placeholder — not yet configured by your agency"
        >
          <i class="fas fa-file-alt"></i>
          {{ route.label || "Agency reporting policy" }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * GuardrailCard — the structured route card under a guardrail refusal.
 *
 * Rendered when an assistant message carries a `guardrail` payload (live SSE
 * `guardrail` event, or `context_metadata.guardrail` on history reload):
 *   { mode, category_class, offers: [], route: {type, link?, phone?, label?} }
 *
 * Calm, professional-boundary styling — deliberately NOT error-red. The
 * refusal text itself is the ordinary message content above this card.
 * Phase 2 adds `offers[]` chips for redirect data pivots.
 */
export default {
  name: "GuardrailCard",
  props: {
    guardrail: {
      type: Object,
      required: true,
    },
  },
  computed: {
    categoryClass() {
      return this.guardrail?.category_class || "clinical_judgment";
    },
    route() {
      return this.guardrail?.route || null;
    },
    icon() {
      switch (this.categoryClass) {
        case "safety":
          return "fas fa-life-ring";
        case "medication":
          return "fas fa-prescription-bottle-alt";
        case "legal":
          return "fas fa-balance-scale";
        case "clarification":
          return "fas fa-question-circle";
        default:
          return "fas fa-shield-alt";
      }
    },
    title() {
      switch (this.categoryClass) {
        case "safety":
          return "Safety concern — escalate to a human";
        case "medication":
          return "Medication questions go to the prescriber";
        case "legal":
          return "Reporting decisions need human judgment";
        case "clarification":
          return "Oliver needs a bit more to go on";
        default:
          return "Your clinical call — Oliver surfaces the data";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.guardrail-card {
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--oliver-border);
  border-left: 3px solid var(--oliver-accent);
  background: var(--oliver-indicator-bg);

  &--safety {
    border-left-color: #e07a3f;
  }
  &--medication,
  &--legal {
    border-left-color: #b58a3a;
  }
}

.guardrail-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.guardrail-card-icon {
  font-size: 13px;
  color: var(--oliver-accent);

  .guardrail-card--safety & {
    color: #e07a3f;
  }
  .guardrail-card--medication &,
  .guardrail-card--legal & {
    color: #b58a3a;
  }
}

.guardrail-card-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--oliver-text);
}

.guardrail-card-body {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.guardrail-card-text {
  font-size: 12.5px;
  line-height: 1.45;
  color: var(--oliver-text-muted);
}

.guardrail-card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.guardrail-card-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  color: var(--oliver-text);
  background: var(--oliver-assistant-bg);
  border: 1px solid var(--oliver-border);
  transition: all 0.15s ease;

  i {
    font-size: 11px;
  }

  &:hover {
    border-color: var(--oliver-accent);
    color: var(--oliver-accent);
    text-decoration: none;
  }

  &--primary {
    background: #e07a3f;
    border-color: #e07a3f;
    color: #fff;

    &:hover {
      background: #c9682f;
      border-color: #c9682f;
      color: #fff;
    }
  }

  // Rendered when the agency hasn't confirmed real contact/policy info yet
  // (guardrail_config.*.configured = false) — same info visible, but inert:
  // no href, no hover feedback, so no one mistakes it for a working link.
  &--placeholder {
    cursor: default;
    opacity: 0.55;

    &:hover {
      border-color: var(--oliver-border);
      color: var(--oliver-text);
    }
  }
}
</style>
